import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Globe, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { PaymentProceedModal } from '../shared/PaymentProceedModal';
import { ErrorModal } from '../../ui/ErrorModal';
import {
  globalServicesService,
  ApiError,
  type GsCountry,
  type GsProduct,
  type GsQuote,
  type GsPurchase,
} from '../../../services/globalServicesService';

const STORAGE_KEY = 'gs:inFlightId';

const FAMILY_LABEL: Record<string, string> = {
  mobile: 'Airtime & data',
  pin: 'PIN / gift',
  utility: 'Utilities',
  esim: 'eSIM',
};

function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `gs-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function fieldLabel(field: string) {
  return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function GlobalServicesOverlay() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'country' | 'family' | 'details' | 'products' | 'fields' | 'pending' | 'done'>('country');
  const [countries, setCountries] = useState<GsCountry[]>([]);
  const [countryIso, setCountryIso] = useState('');
  const [families, setFamilies] = useState<string[]>([]);
  const [family, setFamily] = useState('');
  const [products, setProducts] = useState<GsProduct[]>([]);
  const [selected, setSelected] = useState<GsProduct | null>(null);
  const [quote, setQuote] = useState<GsQuote | null>(null);
  const [purchase, setPurchase] = useState<GsPurchase | null>(null);
  const [lookupHint, setLookupHint] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rangedAmount, setRangedAmount] = useState('');

  const requiredFields = selected?.requiredCreditPartyFields || [];
  const schema = useMemo(() => {
    const shape: Record<string, z.ZodTypeAny> = {};
    for (const field of requiredFields) {
      shape[field] = z.string().min(3, `${fieldLabel(field)} is required`);
    }
    return z.object(shape);
  }, [requiredFields.join('|')]);

  const form = useForm<Record<string, string>>({
    resolver: zodResolver(schema),
    defaultValues: {},
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await globalServicesService.countries();
        if (!cancelled) setCountries(list);
      } catch {
        if (!cancelled) setError('Global Services is not available right now.');
      }
      const inflight = sessionStorage.getItem(STORAGE_KEY);
      if (inflight) {
        try {
          const row = await globalServicesService.getPurchase(Number(inflight));
          if (!cancelled && row) {
            setPurchase(row);
            setStep(row.status === 'completed' || row.status === 'failed' || row.status === 'reversed' ? 'done' : 'pending');
          }
        } catch {
          sessionStorage.removeItem(STORAGE_KEY);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (step !== 'pending' || !purchase?.id) return undefined;
    const timer = setInterval(async () => {
      try {
        const row = await globalServicesService.getPurchase(purchase.id);
        setPurchase(row);
        if (['completed', 'failed', 'reversed'].includes(row.status)) {
          setStep('done');
          if (row.status !== 'completed') sessionStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [step, purchase?.id]);

  const selectCountry = async (iso: string) => {
    setCountryIso(iso);
    setLoading(true);
    try {
      const next = await globalServicesService.families(iso);
      setFamilies(next);
      setStep('family');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load categories');
    } finally {
      setLoading(false);
    }
  };

  const selectFamily = (next: string) => {
    setFamily(next);
    setStep('details');
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const list = await globalServicesService.products(countryIso, family);
      setProducts(list);
      setStep('products');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load products');
    } finally {
      setLoading(false);
    }
  };

  const runLookup = async () => {
    const mobile = form.getValues('mobile_number');
    if (!mobile) return;
    setLoading(true);
    try {
      const result = await globalServicesService.lookup(mobile);
      setLookupHint(result.operatorName
        ? `Suggested operator: ${result.operatorName}. Confirm this is still correct if the number was recently ported.`
        : 'Confirm the operator on the next screen.');
    } catch (err) {
      setLookupHint(err instanceof Error ? err.message : 'Lookup unavailable — choose the operator from the list.');
    } finally {
      setLoading(false);
    }
  };

  const startQuote = async (product: GsProduct) => {
    setSelected(product);
    const needed = product.requiredCreditPartyFields || [];
    const values = form.getValues();
    const missing = needed.filter((f) => !values[f]);
    if (missing.length) {
      setStep('fields');
      return;
    }
    setLoading(true);
    try {
      const isRanged = String(product.type || '').startsWith('RANGED_');
      const quoted = await globalServicesService.quote({
        productId: product.id,
        rangedAmount: isRanged ? Number(rangedAmount) : undefined,
        calculationMode: isRanged ? 'DESTINATION_AMOUNT' : undefined,
      });
      setQuote(quoted);
      setConfirmOpen(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err instanceof Error ? err.message : 'Quote failed'));
    } finally {
      setLoading(false);
    }
  };

  const confirmPurchase = async () => {
    if (!selected || !quote) return;
    setLoading(true);
    try {
      const values = form.getValues();
      const result = await globalServicesService.purchase({
        productId: selected.id,
        idempotencyKey: newIdempotencyKey(),
        quote: quote.quote,
        creditParty: values,
        rangedAmount: quote.rangedAmount || undefined,
        calculationMode: quote.calculationMode || undefined,
      });
      setPurchase(result);
      sessionStorage.setItem(STORAGE_KEY, String(result.id));
      setConfirmOpen(false);
      if (['completed', 'failed', 'reversed'].includes(result.status)) {
        setStep('done');
      } else {
        setStep('pending');
      }
    } catch (err) {
      const payload = err instanceof ApiError ? err.payload : null;
      const fresh = payload && typeof payload === 'object' ? (payload as { freshQuote?: GsQuote }).freshQuote : null;
      if (fresh) {
        setQuote(fresh);
        setError('The price increased. Please confirm the new amount.');
      } else {
        setError(err instanceof Error ? err.message : 'Purchase failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-4 py-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-slate-600 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center gap-2 mb-2">
        <Globe className="w-6 h-6 text-sky-700" />
        <h1 className="text-xl font-semibold text-slate-900">Global Services</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        International airtime, PINs, utilities and eSIM. You pay in Rand from your wallet.
      </p>

      {loading && (
        <div className="flex items-center gap-2 text-sky-700 text-sm mb-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Working…
        </div>
      )}

      {step === 'country' && (
        <div className="space-y-2">
          {countries.map((c) => (
            <button
              key={c.iso}
              type="button"
              onClick={() => selectCountry(c.iso)}
              className="w-full text-left border rounded-xl px-4 py-3"
            >
              <div className="font-medium">{c.name}</div>
              <div className="text-xs text-slate-500">{c.iso}</div>
            </button>
          ))}
          {!countries.length && !loading && (
            <p className="text-sm text-slate-500">No international products are available yet.</p>
          )}
        </div>
      )}

      {step === 'family' && (
        <div className="space-y-2">
          {families.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => selectFamily(f)}
              className="w-full text-left border rounded-xl px-4 py-3 font-medium"
            >
              {FAMILY_LABEL[f] || f}
            </button>
          ))}
        </div>
      )}

      {step === 'details' && (
        <form
          className="space-y-4"
          onSubmit={form.handleSubmit(() => loadProducts())}
        >
          {requiredFields.length === 0 && (
            <p className="text-sm text-slate-500">Choose a product on the next screen. Delivery fields appear after you pick one.</p>
          )}
          {family === 'mobile' && (
            <div>
              <Label htmlFor="mobile_number">Mobile number</Label>
              <Input id="mobile_number" {...form.register('mobile_number')} placeholder="+2348012345678" />
              <Button type="button" variant="outline" className="mt-2" onClick={runLookup}>
                Look up operator
              </Button>
              {lookupHint && <p className="text-xs text-slate-600 mt-2">{lookupHint}</p>}
            </div>
          )}
          {family !== 'mobile' && ['account_number'].map((field) => (
            <div key={field}>
              <Label htmlFor={field}>{fieldLabel(field)}</Label>
              <Input id={field} {...form.register(field)} />
            </div>
          ))}
          <Button type="submit" className="w-full">Continue</Button>
        </form>
      )}

      {step === 'fields' && selected && (
        <form
          className="space-y-4"
          onSubmit={form.handleSubmit(() => startQuote(selected))}
        >
          {requiredFields.map((field) => (
            <div key={field}>
              <Label htmlFor={field}>{fieldLabel(field)}</Label>
              <Input id={field} {...form.register(field)} />
            </div>
          ))}
          <Button type="submit" className="w-full">Get price</Button>
        </form>
      )}

      {step === 'products' && (
        <div className="space-y-3">
          {selected && String(selected.type || '').startsWith('RANGED_') && (
            <div>
              <Label htmlFor="rangedAmount">Amount</Label>
              <Input
                id="rangedAmount"
                value={rangedAmount}
                onChange={(e) => setRangedAmount(e.target.value)}
                inputMode="decimal"
              />
            </div>
          )}
          {products.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => startQuote(p)}
              className="w-full text-left border rounded-xl px-4 py-3"
            >
              <div className="font-medium">{p.name}</div>
              <div className="text-xs text-slate-500">{p.operatorName} · {p.countryName}</div>
            </button>
          ))}
        </div>
      )}

      {step === 'pending' && (
        <div className="text-center py-10">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-sky-700 mb-3" />
          <p>Your international purchase is being processed.</p>
          <p className="text-sm text-slate-500 mt-2">You can leave this screen. We will keep the receipt in History.</p>
        </div>
      )}

      {step === 'done' && purchase && (
        <div className="space-y-3 py-6">
          <h2 className="text-lg font-semibold">
            {purchase.status === 'completed' ? 'Purchase complete' : 'Purchase update'}
          </h2>
          <p className="text-sm text-slate-600">{purchase.productName}</p>
          <p className="text-sm">Status: {purchase.status}</p>
          <p className="text-sm">Amount: R {Number(purchase.customerZar).toFixed(2)}</p>
          {purchase.fulfilment?.pin && (
            <div className="border dashed rounded-xl p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">PIN</p>
              <p className="font-mono text-2xl tracking-widest">{purchase.fulfilment.pin}</p>
            </div>
          )}
          {purchase.fulfilment?.iccid && (
            <p className="text-sm">eSIM ICCID: {purchase.fulfilment.iccid}</p>
          )}
          {purchase.lastErrorMessage && (
            <p className="text-sm text-red-600">{purchase.lastErrorMessage}</p>
          )}
          <Button className="w-full" onClick={() => navigate('/transactions')}>View history</Button>
        </div>
      )}

      <PaymentProceedModal
        open={confirmOpen}
        payload={quote ? {
          amountCents: quote.customerZarCents,
          recipientLabel: quote.productName,
          recipientSubLabel: quote.operatorName,
          contextLabel: 'International product',
          title: 'Confirm Global Services',
        } : null}
        onProceed={confirmPurchase}
        onCancel={() => setConfirmOpen(false)}
        isProcessing={loading}
      />

      <ErrorModal
        isOpen={!!error}
        title="Global Services"
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
}
